import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["menu"]
  static values = { url: String, title: String }

  connect() {
    this._isOpen = false
    this._boundKeydown = this._onKeydown.bind(this)
    this._boundClickOutside = this._onClickOutside.bind(this)
  }

  disconnect() {
    this._close()
  }

  share(event) {
    // Menu hidden by CSS (mobile breakpoint) → use native share API
    if (this._menuHidden() && navigator.share) {
      navigator.share({
        title: this.titleValue,
        url: this.urlValue
      }).catch(() => {})
      return
    }

    // Desktop: toggle popover
    if (this._isOpen) {
      this._close()
    } else {
      this._open()
    }
  }

  copyLink(event) {
    event.preventDefault()
    navigator.clipboard.writeText(this.urlValue).then(() => {
      const btn = event.currentTarget
      const label = btn.querySelector("[data-label]")
      if (!label) return
      const original = label.textContent
      label.textContent = label.dataset.copiedText || "Copied!"
      btn.classList.add("is-copied")
      setTimeout(() => {
        label.textContent = original
        btn.classList.remove("is-copied")
      }, 2000)
    }).catch(() => {})
    this._close()
  }

  openImageShare(event) {
    event.preventDefault()
    this._close()
    // Call the share controller's open() on the same element
    const shareController = this.application.getControllerForElementAndIdentifier(
      this.element, "share"
    )
    if (shareController) shareController.open()
  }

  shareTo(event) {
    event.preventDefault()
    const platform = event.currentTarget.dataset.platform
    const url = encodeURIComponent(this.urlValue)
    const title = encodeURIComponent(this.titleValue)

    const urls = {
      x: `https://twitter.com/intent/tweet?text=${title}+%23TIL&url=${url}`,
      threads: `https://www.threads.net/intent/post?text=${title}+${url}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      reddit: `https://reddit.com/submit?url=${url}&title=${title}`,
      hackernews: `https://news.ycombinator.com/submitlink?u=${url}&t=${title}`
    }

    const shareUrl = urls[platform]
    if (shareUrl) {
      window.open(shareUrl, "_blank", "width=600,height=500,noopener,noreferrer")
    }
    this._close()
  }

  // Private

  _menuHidden() {
    return this.hasMenuTarget &&
      getComputedStyle(this.menuTarget).display === "none"
  }

  _open() {
    if (!this.hasMenuTarget) return
    this._isOpen = true
    this.menuTarget.classList.add("is-open")

    // Update aria
    const trigger = this.element.querySelector("[data-action*='share-menu#share']")
    if (trigger) trigger.setAttribute("aria-expanded", "true")

    // Focus first item
    requestAnimationFrame(() => {
      const first = this.menuTarget.querySelector(".share-menu-item")
      if (first) first.focus()
    })

    document.addEventListener("keydown", this._boundKeydown)
    document.addEventListener("click", this._boundClickOutside, true)
  }

  _close() {
    if (!this.hasMenuTarget) return
    this._isOpen = false
    this.menuTarget.classList.remove("is-open")

    const trigger = this.element.querySelector("[data-action*='share-menu#share']")
    if (trigger) trigger.setAttribute("aria-expanded", "false")

    document.removeEventListener("keydown", this._boundKeydown)
    document.removeEventListener("click", this._boundClickOutside, true)
  }

  _onKeydown(event) {
    if (event.key === "Escape") {
      this._close()
      const trigger = this.element.querySelector("[data-action*='share-menu#share']")
      if (trigger) trigger.focus()
      return
    }

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault()
      const items = [...this.menuTarget.querySelectorAll(".share-menu-item")]
      if (!items.length) return
      const current = document.activeElement
      const idx = items.indexOf(current)
      let next
      if (event.key === "ArrowDown") {
        next = idx < items.length - 1 ? idx + 1 : 0
      } else {
        next = idx > 0 ? idx - 1 : items.length - 1
      }
      items[next].focus()
    }
  }

  _onClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this._close()
    }
  }
}
